import React, { ComponentProps, ReactNode, RefObject, useMemo } from 'react';

import PropTypes from 'prop-types';

import Checkbox from '@adsk/alloy-react-checkbox';
import {
  hasTailwindBackgroundColor,
  hasTailwindTextColor,
  stripUnknownProps,
} from '@adsk/alloy-react-helpers';
import { CheckmarkIcon } from '@adsk/alloy-react-icon';
import { getMenuDynamicStyles, useTheme } from '@adsk/alloy-react-styles';
import { stylePropType, StylableComponent } from '@adsk/alloy-react-theme';
import Tooltip from '@adsk/alloy-react-tooltip';

type InternalMenuItemProps = StylableComponent<HTMLLIElement> & {
  isSelected?: boolean;
  isHighlighted?: boolean;
  isMultiple?: boolean;
  disabled?: boolean;
  text?: ReactNode;
  subtext?: ReactNode;
  renderLeftComponent?: () => ReactNode;
  renderRightComponent?: () => ReactNode;
  textRef?: RefObject<HTMLDivElement | null> | null;
  subtextRef?: RefObject<HTMLDivElement | null> | null;
};

export type MenuOptionProps = InternalMenuItemProps & {
  tooltipProps?: ComponentProps<typeof Tooltip>;
};

const InternalMenuItem = React.forwardRef<HTMLLIElement, InternalMenuItemProps>(
  (
    {
      isSelected,
      isMultiple,
      isHighlighted,
      text: label,
      subtext,
      renderLeftComponent,
      renderRightComponent,
      style,
      textRef,
      subtextRef,
      onClick,
      ...props
    },
    ref,
  ) => {
    const { disabled } = props;
    const { tokens } = useTheme();
    const menuItem = tokens?.component?.menu?.item;
    const ddItem = tokens?.component?.dropdown?.menu?.item;

    const consumerSetsBackground = hasTailwindBackgroundColor(props.className);
    const consumerSetsColor = hasTailwindTextColor(props.className);

    const isSelectedForBg = Boolean(isSelected) && !isMultiple;
    const dynamicBackgrounds = getMenuDynamicStyles({
      backgrounds: menuItem?.backgrounds,
      disabled,
      isHighlighted,
      isSelected: isSelectedForBg,
      isKeyboardNavigation: true,
    });

    const disabledStyle = menuItem?._?.['&:disabled'];

    const liStyles = useMemo(() => {
      const { '&:disabled': _disabled, ...rowStyles } = menuItem?._ ?? {};
      return [
        { ...rowStyles, ...ddItem?._ },
        { height: 'auto', minHeight: menuItem?._?.height },
        isSelected && !disabled ? menuItem?.selected : menuItem?.idle,
        consumerSetsBackground ? undefined : dynamicBackgrounds,
        disabled
          ? {
              cursor: disabledStyle?.cursor,
              opacity: disabledStyle?.opacity,
            }
          : undefined,
        { '&:not(:last-child)': ddItem?.divider },
      ];
    }, [
      menuItem,
      ddItem,
      isSelected,
      consumerSetsBackground,
      dynamicBackgrounds,
      disabled,
      disabledStyle,
    ]);

    return (
      // eslint-disable-next-line jsx-a11y/click-events-have-key-events -- keyboard selection is handled by Downshift at the menu container level
      <li
        ref={ref}
        css={[...liStyles, style]}
        onClick={disabled ? undefined : onClick}
        {...props}
      >
        {isMultiple && (
          <Checkbox
            inputProps={{
              tabIndex: -1,
              'aria-hidden': true,
            }}
            checked={isSelected}
            style={{
              ...ddItem?.checkbox?._,
              ...(subtext && ddItem?.checkbox?.withSubtext),
            }}
            disabled={disabled}
            readOnly
          />
        )}
        {renderLeftComponent && renderLeftComponent()}
        <div
          css={[
            ddItem?.labelColumn?._,
            !subtext && ddItem?.labelColumn?.noSubtext,
          ]}
        >
          {label && (
            <div
              ref={textRef}
              dir="auto"
              css={{
                textOverflow: 'ellipsis',
                overflow: 'hidden',
                whiteSpace: 'nowrap',
                width: '100%',
                color:
                  disabled && !consumerSetsColor
                    ? (disabledStyle?.color ?? 'inherit')
                    : 'inherit',
              }}
            >
              {label}
            </div>
          )}
          {subtext && (
            <div
              ref={subtextRef}
              dir="auto"
              css={[
                ddItem?.subtext?._,
                disabled && ddItem?.subtext?.disabled?.true,
              ]}
            >
              {subtext}
            </div>
          )}
        </div>
        {renderRightComponent
          ? renderRightComponent()
          : !disabled &&
            isSelected &&
            !isMultiple && <CheckmarkIcon style={ddItem?.checkmark} />}
      </li>
    );
  },
);
InternalMenuItem.displayName = 'InternalMenuItem';

const unknownProps = [
  /** Provided by tooltip but not needed */
  'shouldWrapChildren',
  'wrapper',
  /** Provided by dropdown but not needed */
  'item',
  /** Provided when using custom renderMenuItem, but not needed */
  'inputValue',
  'enableSearchHighlight',
];

const MenuItem = React.forwardRef<HTMLLIElement, MenuOptionProps>(
  ({ tooltipProps, ...props }, ref) =>
    tooltipProps ? (
      <Tooltip
        target={ref}
        getTargetProps={() => props}
        renderTarget={(targetProps) => (
          <InternalMenuItem
            {...(stripUnknownProps(
              targetProps,
              unknownProps,
              // Tooltip doesn't infer this type, but because we're using providing the props through getTargetProps we know they're the same
              // If Tooltip was made generic, it should be able to infer this and we could remove this cast
            ) as InternalMenuItemProps)}
          />
        )}
        {...tooltipProps}
      />
    ) : (
      <InternalMenuItem ref={ref} {...stripUnknownProps(props, unknownProps)} />
    ),
);

MenuItem.displayName = 'MenuOption';

MenuItem.propTypes = {
  /** Styles applied to the root element */
  style: stylePropType,
  /** Class applied to the root element */
  className: PropTypes.string,
  /** Whether menu option is currently selected */
  isSelected: PropTypes.bool,
  /** Whether menu option is currently highlighted.  Used when cursor navigation */
  isHighlighted: PropTypes.bool,
  /** Whether menu option is in a multi select */
  isMultiple: PropTypes.bool,
  /** Whether menu option is disabled */
  disabled: PropTypes.bool,
  /** Primary content */
  text: PropTypes.any,
  /** Secondary content */
  subtext: PropTypes.any,
  /** Props to add and control a menu item Tooltip.  Leave undefined to not have a Tooltip */
  tooltipProps: PropTypes.any,
  /** component to render to the left of the item */
  renderLeftComponent: PropTypes.func,
  /** component to render to the right of the item */
  renderRightComponent: PropTypes.func,
  /** Function executed on click */
  onClick: PropTypes.func,
};

export default MenuItem;
